import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isAuthEndpoint = error.config?.url === '/auth/verify-login-code';
    if (error.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Let the router/ProtectedRoute handle the redirect so public pages
      // (e.g. /confirm, /prep) are not incorrectly sent to /login
      const publicPaths = ['/confirm', '/prep'];
      const isPublicPath = publicPaths.some(p => window.location.pathname.startsWith(p));
      if (!isPublicPath) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const authApi = {
  logout: () => api.post('/logout'),
  getMe: () => api.get('/me'),
  requestLoginCode: (email) => api.post('/auth/request-login-code', { email }),
  verifyLoginCode: (email, code) => api.post('/auth/verify-login-code', { email, code })
};

// Research request endpoints
export const requestsApi = {
  getAll: () => api.get('/requests'),
  getOne: (id) => api.get(`/requests/${id}`),
  create: (data) => api.post('/requests', data),
  update: (id, data) => api.put(`/requests/${id}`, data),
  delete: (id) => api.delete(`/requests/${id}`),
  runNow: (id) => api.post(`/requests/${id}/run-now`),
  getResponses: (id, page = 1) => api.get(`/requests/${id}/responses?page=${page}`),
  getLatestResults: () => api.get('/requests/latest-results'),
  getAllResults: (page = 1, requestId) => api.get(`/requests/all-results?page=${page}${requestId ? `&request_id=${requestId}` : ''}`),
  markResultRead: (responseId) => api.patch(`/requests/responses/${responseId}/read`),
  markAllRead: (requestId) => api.patch(`/requests/responses/mark-all-read${requestId ? `?request_id=${requestId}` : ''}`),
  deleteResult: (responseId) => api.delete(`/requests/responses/${responseId}`)
};

// Reminders endpoints
export const remindersApi = {
  getAll: () => api.get('/reminders'),
  getOne: (id) => api.get(`/reminders/${id}`),
  create: (data) => api.post('/reminders', data),
  update: (id, data) => api.put(`/reminders/${id}`, data),
  delete: (id) => api.delete(`/reminders/${id}`)
};

// Public confirmation endpoint (no auth required)
const publicApi = axios.create({ baseURL: API_BASE_URL });
export const confirmApi = {
  confirm: (token) => publicApi.get(`/confirm/${token}`)
};

// 211 endpoints
export const twoOneOneApi = {
  currentSession: () => api.get('/211/sessions/current'),
  createSession: () => api.post('/211/sessions'),
  sessionStatus: (sessionId) => api.get(`/211/sessions/${sessionId}/status`),
  getSessions: () => api.get('/211/sessions'),
  deleteSession: (id) => api.delete(`/211/sessions/${id}`),
  extendSession: (id) => api.post(`/211/sessions/${id}/extend`),
  manualConfirm: (sessionId, reminderId) => api.post(`/211/sessions/${sessionId}/reminders/${reminderId}/confirm`),
  closeSession: (id) => api.patch(`/211/sessions/${id}/close`),
  reopenSession: (id) => api.patch(`/211/sessions/${id}/reopen`),
  updateComment: (id, comment) => api.patch(`/211/sessions/${id}/comment`, { comment }),
};

// PCTrader endpoints
export const pctraderApi = {
  getTrades: () => api.get('/pctrader/trades'),
  getWatchPairs: () => api.get('/pctrader/watchpairs'),
};

// Settings endpoints
export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data)
};

// Price check endpoints
export const priceChecksApi = {
  getAll: () => api.get('/price-checks'),
  create: (data) => api.post('/price-checks', data),
  update: (id, data) => api.put(`/price-checks/${id}`, data),
  delete: (id) => api.delete(`/price-checks/${id}`),
  getResults: (id, page = 1) => api.get(`/price-checks/${id}/results?page=${page}`),
  runNow: (id) => api.post(`/price-checks/${id}/run-now`),
};

// Prompt endpoints
export const promptsApi = {
  getAll: () => api.get('/prompts'),
  getResults: (promptId) => api.get(`/prompts/${promptId}/results`),
  create: (data) => api.post('/prompts', data),
  update: (promptId, data) => api.put(`/prompts/${promptId}`, data),
  delete: (promptId) => api.delete(`/prompts/${promptId}`),
};

// Hermes agent (usecase pipeline runs) endpoints
export const hermesAgentApi = {
  getAll: () => api.get('/hermes-agent'),
  getOne: (id) => api.get(`/hermes-agent/${id}`),
  create: (data) => api.post('/hermes-agent', data),
  update: (id, data) => api.put(`/hermes-agent/${id}`, data),
  delete: (id) => api.delete(`/hermes-agent/${id}`),
};

// Posting ideas endpoints
export const postingIdeasApi = {
  getAll: () => api.get('/posting-ideas'),
  generateIdeas: (id) => api.post(`/posting-ideas/${id}/generate`),
  getIdeas: (id) => api.get(`/posting-ideas/${id}/ideas`),
};

// Pulseboard (PulseChain dashboard) endpoints
export const pulseboardApi = {
  prices: () => api.get('/pulseboard/prices'),
  portfolio: (wallets) => api.post('/pulseboard/portfolio', { wallets }),
  telegram: (payload) => api.post('/pulseboard/telegram', payload),
  health: () => api.get('/pulseboard/health'),
  getSettings: () => api.get('/pulseboard/settings'),
  updateSettings: (data) => api.put('/pulseboard/settings', data),
  updateExclusions: (excludedTokens) => api.put('/pulseboard/exclusions', { excludedTokens }),
};

// Analytics endpoints
export const analyticsApi = {
  listSites: () => api.get('/analytics/sites'),
  createSite: (data) => api.post('/analytics/sites', data),
  getStats: (siteId, range) => api.get(`/analytics/stats?site=${encodeURIComponent(siteId)}&range=${encodeURIComponent(range)}`)
};

export default api;
